import React from "react";

import { useFocusTrap } from "../../components/Modal";
import { getActivitySessions, getBreakdown } from "../../lib/api";
import type { AuthHeaders } from "../../lib/auth";
import type {
  ActivitySession,
  InsightDimension,
  InsightFilters,
} from "../../lib/types";
import { SessionTimeline } from "./ActivityPanel";
import { getHashParam, setHashParam } from "../../lib/hashState";
import { ROUTE_EVENT } from "../../shell/route";
import { usePreset } from "./range";
import {
  Button,
  Card,
  Delta,
  EmptyState,
  Grid,
  IconButton,
  Pill,
  RowButton,
  Skeleton,
  StatTile,
  Table,
} from "../../design";
import { BarList, TimeChart } from "./shared";
import {
  DIMENSION_LABELS,
  OTHER,
  WORK,
  humanize,
  pct,
  previousRange,
  shortDay,
  shortRepo,
  timeOf,
  usdShort,
  useLoad,
  type ActiveFilter,
} from "./util";

export type EntityKind = Exclude<InsightDimension, "none" | "cost_basis">;
export type EntityRef = { kind: EntityKind; id: string; label: string };

type Ctx = { open: (e: EntityRef) => void };
const EntityCtx = React.createContext<Ctx | null>(null);

/** Opens the detail drawer for a person, team, repo, model, agent or client;
 * null when no EntityHost is above (rows then stay plain). */
export function useEntity(): Ctx | null {
  return React.useContext(EntityCtx);
}

const FILTER_KEY: Record<EntityKind, keyof InsightFilters> = {
  user: "user",
  team: "team",
  repo: "repo",
  model: "model",
  kind: "kind",
  client: "client",
  tenant: "tenant",
  feature: "feature",
  env: "env",
  key: "key",
  device: "device",
};

const KINDS: EntityKind[] = [
  "user",
  "team",
  "repo",
  "model",
  "kind",
  "client",
  "tenant",
  "feature",
  "env",
  "key",
  "device",
];

function parseOpen(v: string | null): EntityRef | null {
  if (!v) return null;
  const [kind, id, ...label] = v.split(":");
  return KINDS.includes(kind as EntityKind) && id
    ? { kind: kind as EntityKind, id, label: label.join(":") || id }
    : null;
}

export function toFilter(e: EntityRef): ActiveFilter {
  return {
    key: FILTER_KEY[e.kind],
    value: e.id,
    label: `${DIMENSION_LABELS[e.kind]}: ${e.kind === "repo" ? shortRepo(e.label) : e.label}`,
  };
}

/** Hosts one detail drawer for everything rendered inside it. */
export function EntityHost({
  authHeaders,
  base,
  orgId,
  onFilter,
  children,
}: {
  authHeaders: AuthHeaders;
  base: string;
  /** Set on org pages: a person's drawer then also shows their skill history. */
  orgId?: string;
  /** "Show everywhere": filter the analytics pages to this entity. */
  onFilter?: (f: ActiveFilter) => void;
  children: React.ReactNode;
}) {
  const [open, setOpenState] = React.useState<EntityRef | null>(() =>
    parseOpen(getHashParam("open")),
  );
  const setOpen = React.useCallback((e: EntityRef | null) => {
    setOpenState(e);
    setHashParam("open", e ? `${e.kind}:${e.id}:${e.label}` : null);
  }, []);
  const ctx = React.useMemo(() => ({ open: setOpen }), [setOpen]);
  // Moving to another page closes the drawer instead of leaving it over it.
  React.useEffect(() => {
    let route = window.location.hash.split("?")[0];
    const onHash = () => {
      const next = window.location.hash.split("?")[0];
      if (next !== route) setOpenState(null);
      route = next;
    };
    const events = ["hashchange", "popstate", ROUTE_EVENT];
    events.forEach((e) => window.addEventListener(e, onHash));
    return () => events.forEach((e) => window.removeEventListener(e, onHash));
  }, []);
  return (
    <EntityCtx.Provider value={ctx}>
      {children}
      {open && (
        <EntityDrawer
          key={`${open.kind}:${open.id}`}
          authHeaders={authHeaders}
          base={base}
          orgId={orgId}
          entity={open}
          onClose={() => setOpen(null)}
          onFilter={
            onFilter &&
            (() => {
              onFilter(toFilter(open));
              setOpen(null);
            })
          }
        />
      )}
    </EntityCtx.Provider>
  );
}

// What the drawer breaks an entity down by: the two dimensions that say
// most about it.
const SPLITS: Record<EntityKind, [InsightDimension, InsightDimension]> = {
  user: ["model", "repo"],
  team: ["user", "model"],
  repo: ["user", "model"],
  model: ["user", "repo"],
  kind: ["user", "model"],
  client: ["user", "model"],
  tenant: ["feature", "model"],
  feature: ["tenant", "model"],
  env: ["tenant", "model"],
  key: ["tenant", "model"],
  device: ["model", "repo"],
};

// Personal views have no people to split by; use another dimension instead.
export function splitsFor(
  kind: EntityKind,
  org: boolean,
): [InsightDimension, InsightDimension] {
  const pair = SPLITS[kind];
  if (org) return pair;
  const spare = (
    ["repo", "model", "kind", "client"] as InsightDimension[]
  ).find((d) => d !== kind && !pair.includes(d))!;
  return pair.map((d) => (d === "user" || d === "team" ? spare : d)) as [
    InsightDimension,
    InsightDimension,
  ];
}

function EntityDrawer({
  authHeaders,
  base,
  orgId,
  entity,
  onClose,
  onFilter,
}: {
  authHeaders: AuthHeaders;
  base: string;
  orgId?: string;
  entity: EntityRef;
  onClose: () => void;
  onFilter?: () => void;
}) {
  const panel = React.useRef<HTMLDivElement>(null);
  useFocusTrap(panel, onClose);
  const [, , range] = usePreset();
  const [session, setSession] = React.useState<ActivitySession | null>(null);
  const flt: InsightFilters = { [FILTER_KEY[entity.kind]]: entity.id };
  const [a, b] = splitsFor(entity.kind, !!orgId);
  const { data, error } = useLoad(
    () =>
      Promise.all([
        getBreakdown(authHeaders, base, range, "none", flt),
        getBreakdown(authHeaders, base, previousRange(range), "none", flt),
        getBreakdown(authHeaders, base, range, a, flt),
        getBreakdown(authHeaders, base, range, b, flt),
        getActivitySessions(authHeaders, base, range, flt).catch(() => []),
      ]),
    [base, range.start, range.end, entity.kind, entity.id],
  );
  const name = entity.kind === "repo" ? shortRepo(entity.label) : entity.label;

  let body: React.ReactNode;
  if (error)
    body = (
      <EmptyState icon="alert" title="Couldn't load this">
        {error}
      </EmptyState>
    );
  else if (!data)
    body = (
      <>
        <Skeleton height={88} />
        <Skeleton height={180} />
      </>
    );
  else {
    const [now, before, byA, byB, sessions] = data;
    const t = now.totals;
    const p = before.totals;
    const fail = t.requests ? t.errors / t.requests : null;
    const rows = (d: InsightDimension, g: typeof byA) =>
      g.groups
        .filter((x) => Number(x.metrics.cost_usd) > 0)
        .slice(0, 6)
        .map((x) => ({
          key: x.key ?? OTHER,
          label: d === "repo" ? shortRepo(x.label) : x.label,
          value: Number(x.metrics.cost_usd),
        }));
    body = (
      <>
        <Grid cols={2}>
          <StatTile
            label="Spend"
            value={usdShort(t.cost_usd)}
            delta={
              <Delta
                now={Number(t.cost_usd)}
                prev={Number(p.cost_usd)}
                neutral
              />
            }
          />
          <StatTile
            label="Sessions"
            value={humanize(t.sessions)}
            delta={<Delta now={t.sessions} prev={p.sessions} neutral />}
          />
          <StatTile
            label="Failed requests"
            value={pct(fail, 1)}
            note={`${humanize(t.requests)} requests`}
          />
          <StatTile
            label="Cache hit"
            value={pct(t.cache_hit_ratio)}
            note={`Saved ${usdShort(t.saved_usd)}`}
          />
        </Grid>
        <Card title="Cost per day">
          <TimeChart
            days={now.series.map((d) => ({
              day: d.day,
              values: {
                cost: Object.values(d.values).reduce(
                  (s, v) => s + Number(v),
                  0,
                ),
              },
            }))}
            order={["cost"]}
            colorOf={() => "var(--ds-s1)"}
            labelOf={() => "Spend"}
            compact
          />
        </Card>
        <Grid cols={2} className="ana-split">
          {(
            [
              [a, byA],
              [b, byB],
            ] as const
          ).map(([d, g]) => (
            <Card key={d} title={`By ${DIMENSION_LABELS[d].toLowerCase()}`}>
              {rows(d, g).length ? (
                <BarList rows={rows(d, g)} format={usdShort} />
              ) : (
                <EmptyState compact title="Nothing in this range" />
              )}
            </Card>
          ))}
        </Grid>
        <Card
          flush
          title="Recent sessions"
          info="Sessions link to their requests for 7 days, so older spend shows in the totals but not here."
          actions={<span className="ds-meta">{sessions.length} in range</span>}
        >
          {sessions.length === 0 ? (
            <EmptyState compact title="No sessions reported in this range" />
          ) : (
            <Table size="sm" label="Recent sessions">
              <tbody>
                {sessions.slice(0, 8).map((s) => (
                  <RowButton
                    key={s.id}
                    label={`Open the timeline of ${s.repo ?? "this session"}`}
                    onOpen={() => setSession(s)}
                  >
                    <td className="ana-ellipsis-cell">
                      <span className="strong">
                        {s.repo ? shortRepo(s.repo) : "No repo"}
                      </span>
                      <span className="ds-cell-sub">
                        {[s.email, timeOf(s.started_at)]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </td>
                    <td>
                      <Pill>{WORK[s.work_type] ?? s.work_type}</Pill>
                    </td>
                    <td className="num strong">{usdShort(s.cost_usd)}</td>
                  </RowButton>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </>
    );
  }

  return (
    <div className="ana-drawer-scrim" onClick={onClose}>
      <aside
        ref={panel}
        className="ana-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={name}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ana-drawer-head">
          <div className="ana-drawer-titles">
            <span className="ds-meta">{DIMENSION_LABELS[entity.kind]}</span>
            <h2 className="ana-drawer-title">{name}</h2>
            <span className="ds-meta">
              {shortDay(range.start)} – {shortDay(range.end)} · vs the previous
              period
            </span>
          </div>
          <div className="ana-chips">
            {onFilter && (
              <Button size="sm" icon="filter" onClick={onFilter}>
                Filter pages to this
              </Button>
            )}
            <IconButton icon="x" label="Close" onClick={onClose} />
          </div>
        </header>
        {orgId && entity.kind === "user" && (
          <div className="ana-drawer-notice">
            <Pill tone="info" icon="lock">
              Opening a person&apos;s activity is recorded in the audit log
            </Pill>
            <span className="ds-meta">
              They can see what admins see from My usage.
            </span>
          </div>
        )}
        <div className="ana-drawer-body">{body}</div>
        {session && (
          <SessionTimeline
            authHeaders={authHeaders}
            base={base}
            session={session}
            onClose={() => setSession(null)}
          />
        )}
      </aside>
    </div>
  );
}
